import { api } from '@/lib/api';
import type { ContextOperatorFilterUser } from '@/shared/utils/contextOperators';
import {
  LeadResponse,
  LeadQuery,
  LeadListResponse,
  CreateLeadRequest,
  UpdateLeadRequest,
  ConvertLeadRequest,
  TransferRequest,
  LeadKanbanBoard,
  KanbanColumnPage,
  LeadKanbanCard,
  LeadKanbanQuery,
  LeadKanbanColumnQuery,
  LeadCouponsResetResponse
} from '@troia-v3/types';

/**
 * Leads Service
 *
 * Service layer para comunicação com API de leads.
 * Todos os métodos retornam dados desempacotados (response.data.data).
 */
/** Um lead na lista de escolha: nome do contato e onde ele está no funil. */
export interface LeadDropdownItem {
  id: string;
  label: string;
  sublabel?: string;
}

export interface LeadDropdownPage {
  items: LeadDropdownItem[];
  total: number;
}

export const leadsService = {
  /**
   * Buscar lista de leads com filtros e paginação
   */
  async getLeads(query?: LeadQuery): Promise<LeadListResponse> {
    const response = await api.get('/leads', { params: query });
    return response.data.data;
  },

  /**
   * Buscar lead específico por ID
   */
  async getLeadById(id: string): Promise<LeadResponse> {
    const response = await api.get(`/leads/${id}`);
    return response.data.data;
  },

  /**
   * Criar novo lead
   */
  async createLead(data: CreateLeadRequest, options?: { handleErrorLocally?: boolean }): Promise<LeadResponse> {
    const response = await api.post('/leads', data, options?.handleErrorLocally ? { _handleErrorLocally: true } as any : undefined);
    return response.data.data;
  },

  /**
   * Atualizar lead existente
   */
  async updateLead(id: string, data: UpdateLeadRequest, options?: { handleErrorLocally?: boolean }): Promise<LeadResponse> {
    const response = await api.put(`/leads/${id}`, data, options?.handleErrorLocally ? { _handleErrorLocally: true } as any : undefined);
    return response.data.data;
  },

  /**
   * Excluir lead
   */
  async deleteLead(id: string): Promise<void> {
    await api.delete(`/leads/${id}`);
  },

  /**
   * Converter lead em cliente
   */
  async convertLead(id: string, data: ConvertLeadRequest): Promise<LeadResponse> {
    const response = await api.post(`/leads/${id}/convert`, data);
    return response.data.data;
  },

  /**
   * Transferir lead para outro usuário ou time (ou desatribuir)
   *
   * Endpoint padronizado: `POST /leads/:id/transfer`
   * Body: `{ toUserId?, toTeamId?, reason?, notes? }` — XOR entre user/team, null desatribui.
   */
  async transfer(id: string, body: TransferRequest): Promise<LeadResponse> {
    const response = await api.post(`/leads/${id}/transfer`, body);
    return response.data.data;
  },

  /**
   * Mover lead para outra etapa do funil
   */
  async moveLeadToStep(id: string, stepId: string): Promise<LeadResponse> {
    const response = await api.put(`/leads/${id}`, { stepId });
    return response.data.data;
  },

  /**
   * Marcar lead como ganho
   */
  async markAsWon(id: string, wonValue?: number): Promise<LeadResponse> {
    const response = await api.put(`/leads/${id}`, {
      businessStatus: 'won',
      wonValue,
      wonDate: new Date().toISOString()
    });
    return response.data.data;
  },

  /**
   * Marcar lead como perdido
   */
  async markAsLost(id: string, lostReason?: string): Promise<LeadResponse> {
    const response = await api.put(`/leads/${id}`, {
      businessStatus: 'lost',
      lostReason,
      lostDate: new Date().toISOString()
    });
    return response.data.data;
  },

  /**
   * Buscar leads de um contato específico
   */
  async getLeadsByContact(contactId: string): Promise<LeadResponse[]> {
    const response = await api.get(`/leads/contact/${contactId}`);
    return response.data.data;
  },

  /**
   * Buscar leads de um cliente específico
   */
  async getLeadsByCustomer(customerId: string): Promise<LeadResponse[]> {
    const response = await api.get(`/leads/customer/${customerId}`);
    return response.data.data;
  },

  /**
   * Buscar leads atribuídos a um usuário específico
   */
  async getLeadsByAssignee(userId: string): Promise<LeadResponse[]> {
    const response = await api.get(`/leads/assigned/${userId}`);
    return response.data.data;
  },

  /**
   * Buscar leads de um canal específico
   */
  async getLeadsByChannel(channelId: string): Promise<LeadResponse[]> {
    const response = await api.get(`/leads/channel/${channelId}`);
    return response.data.data;
  },

  /**
   * Buscar leads de uma fonte específica
   */
  async getLeadsBySource(source: string): Promise<LeadResponse[]> {
    const response = await api.get(`/leads/source/${source}`);
    return response.data.data;
  },

  /**
   * Zerar os cupons de sorteio: limpa os cupons de TODOS os leads da empresa e
   * a numeração recomeça do 1. Sem volta — a tela pede para exportar antes.
   */
  async resetCoupons(): Promise<LeadCouponsResetResponse> {
    const response = await api.post('/leads/coupons/reset');
    return response.data.data;
  },

  /**
   * Exportar leads para CSV com filtros aplicados
   */
  async exportLeads(query?: Partial<LeadQuery>): Promise<void> {
    try {
      // Backend retorna CSV como texto direto (não JSON). Filtros vão no mesmo
      // formato aninhado da listagem (`filters[...]` + search) — o export
      // devolve exatamente o conjunto que a listagem mostra.
      const response = await api.get('/leads/export', {
        params: { search: query?.search, filters: query?.filters },
        responseType: 'blob', // Receber como blob para download
      });

      // Criar URL do blob
      const blob = new Blob([response.data], { type: 'text/csv; charset=utf-8' });
      const url = window.URL.createObjectURL(blob);

      // Criar link temporário e acionar download
      const link = document.createElement('a');
      link.href = url;
      link.download = `leads-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();

      // Limpar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar leads:', error);
      throw error;
    }
  },

  /**
   * Importar leads via arquivo (CSV, TXT, HTML)
   * Utiliza processamento com IA para extrair leads estruturados
   */
  async importLeads(stepId: string, file: File, assignmentConfig?: any): Promise<{ jobId: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // ✅ Adicionar assignmentConfig ao FormData se fornecido
      if (assignmentConfig) {
        formData.append('assignmentConfig', JSON.stringify(assignmentConfig));
      }

      const response = await api.post(`/leads/${stepId}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data.data;
    } catch (error) {
      console.error('Erro ao importar leads:', error);
      throw error;
    }
  },

  /** Lista com busca para escolher UM lead (`GET /leads/dropdown`, mesma visibilidade do CRM). */
  async getLeadsDropdown(search?: string): Promise<LeadDropdownPage> {
    const response = await api.get('leads/dropdown', { params: search ? { search } : undefined });
    return response.data.data;
  },

  /** O lead já escolhido — rótulo ao reabrir a tela (`GET /leads/dropdown/:id`). */
  async getLeadDropdownItem(id: string): Promise<LeadDropdownItem> {
    const response = await api.get(`leads/dropdown/${id}`);
    return response.data.data;
  },

  async getCampaignNamesDropdown(): Promise<string[]> {
    const response = await api.get('leads/campaigns/dropdown');
    return response.data.data;
  },

  async getAdsetNamesDropdown(): Promise<string[]> {
    const response = await api.get('leads/adsets/dropdown');
    return response.data.data;
  },

  async getAdNamesDropdown(): Promise<string[]> {
    const response = await api.get('leads/ads/dropdown');
    return response.data.data;
  },

  async getOriginsDropdown(): Promise<string[]> {
    const response = await api.get('leads/origins/dropdown');
    return response.data.data;
  },

  /**
   * Tags para o filtro do CRM — vem do CATÁLOGO (`/tags/dropdown`), não mais
   * do `distinct` sobre contatos (`/contacts/tags`, mantido só por
   * retrocompatibilidade). Diferença prática: tag recém-cadastrada aparece no
   * filtro mesmo antes de alguém ter sido marcado com ela.
   */
  async getTagsDropdown(): Promise<string[]> {
    const response = await api.get('tags/dropdown');
    return (response.data.data as Array<{ name: string }>).map((tag) => tag.name);
  },

  async getTypesDropdown(): Promise<string[]> {
    const response = await api.get('leads/types/dropdown');
    return response.data.data;
  },

  /** Quem pode ser responsável por lead do funil (participantes em qualquer papel). */
  async getAssigneesForFunnel(funnelId: string): Promise<ContextOperatorFilterUser[]> {
    const response = await api.get('leads/assignees/dropdown', { params: { funnelId } });
    return response.data.data;
  },

  /** Operadores do filtro para os funis escolhidos (participantes + responsáveis por leads deles). */
  async getOperatorsForFunnels(funnelIds: string[], scope?: 'team' | 'all'): Promise<ContextOperatorFilterUser[]> {
    const response = await api.get('leads/operators/dropdown', {
      params: { funnelIds: funnelIds.join(','), ...(scope ? { scope } : {}) },
    });
    return response.data.data;
  },

  // ============================================================================
  // INTERESTS (Database Documents)
  // ============================================================================

  async getLeadInterests(leadId: string): Promise<any[]> {
    const response = await api.get(`leads/${leadId}/interests`);
    return response.data.data;
  },

  async addLeadInterests(leadId: string, documentIds: string[]): Promise<void> {
    await api.post(`leads/${leadId}/interests`, { documentIds });
  },

  async removeLeadInterest(leadId: string, documentId: string): Promise<void> {
    await api.delete(`leads/${leadId}/interests/${documentId}`);
  },

  async approveLeadInterest(leadId: string, documentId: string): Promise<void> {
    await api.put(`leads/${leadId}/interests/${documentId}/approve`);
  },

  // ============================================================================
  // KANBAN
  // ============================================================================

  /**
   * Buscar board kanban completo (colunas com janela inicial de cards).
   * Envelope específico de leads: o board genérico + `stepThresholds` por etapa.
   */
  async getKanbanBoard(params: LeadKanbanQuery): Promise<LeadKanbanBoard> {
    const response = await api.get('/leads/kanban', { params });
    return response.data.data;
  },

  /**
   * Buscar página adicional de uma coluna do kanban (paginação por cursor)
   */
  async getKanbanColumn(params: LeadKanbanColumnQuery): Promise<KanbanColumnPage<LeadKanbanCard>> {
    const response = await api.get('/leads/kanban/column', { params });
    return response.data.data;
  },

  /**
   * Atualizar stepId e/ou kanbanRank de um lead (move/reorder no kanban)
   */
  async updateLeadKanban(id: string, data: { stepId?: string; kanbanRank: string }): Promise<LeadResponse> {
    const response = await api.put(`/leads/${id}`, data);
    return response.data.data;
  },

  // ============================================================================
  // ALERTAS DE INATIVIDADE
  // ============================================================================

  /**
   * Silenciar (ou reativar, com `until: null`) o alerta de inatividade do lead.
   */
  async snoozeAlert(id: string, until: string | null): Promise<LeadResponse> {
    const response = await api.post(`/leads/${id}/alert-snooze`, { until });
    return response.data.data;
  },
};
